// Plain typed fetch wrapper for the joystudy API. We don't pull `@joystudy/api`
// types into the web app because the API tsconfig leans on bun + Elysia types
// that don't apply in the browser. Instead we mirror the response shapes here
// — small surface, easy to keep in sync, no cross-workspace type leakage.

const API_ORIGIN =
  (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? 'http://localhost:3000';

export const apiOrigin = API_ORIGIN;

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  // Don't force application/json on multipart bodies — the browser must set
  // content-type with the boundary string.
  const isFormData = init?.body instanceof FormData;
  const headers: HeadersInit = isFormData
    ? { ...(init?.headers ?? {}) }
    : { 'content-type': 'application/json', ...(init?.headers ?? {}) };
  return fetch(`${API_ORIGIN}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });
}

export async function apiJson<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, init);
  const text = await res.text();
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error: unknown }).error)
        : `request_failed_${res.status}`;
    throw new ApiError(message, res.status, body);
  }
  return body as T;
}
